/**
 * One request per key, however many components ask.
 *
 * Three independent hooks fetch /api/marketplace/apps and two fetch
 * /api/sidebar/user-menu. Mounted together they produce the same request several times on
 * every page load — a 46-second window of one real session showed user-menu four times and
 * marketplace seven. Each is a Mongo round trip behind the auth gate, and none of the
 * callers needs a different answer from the others.
 *
 * `inflight` is what actually collapses them. A result cache alone still lets two
 * components mounted in the same tick both miss and both fetch, which is exactly the case
 * here — the hub page and the layout inside it mount together.
 *
 * Failures are never cached: the entry is dropped so the next caller retries rather than
 * inheriting a rejection for the rest of the TTL.
 */

const DEFAULT_TTL_MS = 5 * 60 * 1000;

interface Entry<T> {
  at: number;
  value: T;
}

const cache = new Map<string, Entry<unknown>>();
const inflight = new Map<string, Promise<unknown>>();

export async function dedupedFetch<T>(
  key: string,
  fetcher: () => Promise<T>,
  ttlMs: number = DEFAULT_TTL_MS,
): Promise<T> {
  const hit = cache.get(key);
  if (hit && Date.now() - hit.at < ttlMs) return hit.value as T;

  const pending = inflight.get(key);
  if (pending) return pending as Promise<T>;

  const promise = fetcher()
    .then((value) => {
      cache.set(key, { at: Date.now(), value });
      return value;
    })
    .finally(() => {
      inflight.delete(key);
    });

  inflight.set(key, promise);
  return promise;
}

/**
 * Drop cached answers. Call after a mutation that changes what these endpoints return —
 * the sidebar's app-selection save is the one that matters today.
 */
export function invalidateCached(keyPrefix?: string): void {
  if (!keyPrefix) {
    cache.clear();
    return;
  }
  for (const key of Array.from(cache.keys())) {
    if (key.startsWith(keyPrefix)) cache.delete(key);
  }
}
